import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import type { GuideStep } from './guide-types'

interface TourOverlayProps {
  title: string
  steps: GuideStep[]
  startStepId: string
  onStep: (stepId: string) => void
  onEnd: (status: 'completed' | 'skipped' | 'in_progress', stepId: string) => void
}

export function TourOverlay({ title, steps, startStepId, onStep, onEnd }: TourOverlayProps) {
  const visibleSteps = useMemo(
    () => steps.filter((step) => step.optional || document.querySelector(step.target)),
    [steps],
  )
  const startIndex = Math.max(0, visibleSteps.findIndex((step) => step.id === startStepId))
  const [index, setIndex] = useState(startIndex)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const step = visibleSteps[index]

  useEffect(() => {
    if (!step) return
    const target = document.querySelector(step.target)
    if (!target) {
      if (step.optional && index < visibleSteps.length - 1) setIndex((value) => value + 1)
      return
    }

    const update = () => {
      const next = target.getBoundingClientRect()
      setRect(next)
    }
    update()
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    onStep(step.id)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [index, step, visibleSteps.length, onStep])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && step) onEnd('in_progress', step.id)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onEnd, step])

  if (!step || !rect) return null

  const tooltipTop = Math.min(window.innerHeight - 250, Math.max(16, rect.bottom + 16))
  const tooltipLeft = Math.min(window.innerWidth - 340, Math.max(16, rect.left))

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="pointer-events-none absolute rounded-xl border-2 border-primary bg-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] transition-all"
        style={{
          left: Math.max(8, rect.left - 8),
          top: Math.max(8, rect.top - 8),
          width: Math.min(window.innerWidth - 16, rect.width + 16),
          height: Math.min(window.innerHeight - 16, rect.height + 16),
        }}
      />
      <div
        className="absolute w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl border bg-popover p-5 text-popover-foreground shadow-2xl"
        style={{ top: tooltipTop, left: tooltipLeft }}
      >
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="font-bold text-primary">{title}</span>
          <span className="text-muted-foreground">Step {index + 1} of {visibleSteps.length}</span>
        </div>
        <h2 className="mt-3 text-base font-bold">{step.title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{step.content}</p>
        <div className="mt-5 flex items-center justify-between gap-2 border-t pt-4">
          <Button type="button" size="sm" variant="ghost" onClick={() => onEnd('in_progress', step.id)}>Pause</Button>
          <div className="flex gap-2">
            {index > 0 ? <Button type="button" size="sm" variant="outline" onClick={() => setIndex((value) => value - 1)}>Back</Button> : null}
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (index === visibleSteps.length - 1) onEnd('completed', step.id)
                else setIndex((value) => value + 1)
              }}
            >
              {index === visibleSteps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
        <button type="button" className="mt-3 text-xs text-muted-foreground underline" onClick={() => onEnd('skipped', step.id)}>Skip guide</button>
      </div>
    </div>
  )
}
